import type { CSSProperties, ReactNode } from 'react';

export interface TableColumn<Row> {
  id: string;
  header: ReactNode;
  width?: string;
  render: (row: Row) => ReactNode;
}

export interface TableProps<Row> {
  rows: readonly Row[];
  columns: readonly TableColumn<Row>[];
  rowKey: (row: Row) => string;
  ariaLabel: string;
  caption?: string;
}

/**
 * A generic dumb component to render a table with rows and columns based on the provided props.
 */
export function Table<Row>({
  rows,
  columns,
  rowKey,
  ariaLabel,
  caption,
}: Readonly<TableProps<Row>>) {
  return (
    <div className="table-container mb-4">
      <section className="items-container" aria-label={ariaLabel}>
        <table className="table items">
          {caption && <caption className="sr-only">{caption}</caption>}
          <thead>
            <tr>
              {columns.map((column) => (
                <th
                  key={column.id}
                  scope="col"
                  style={
                    column.width
                      ? ({ width: column.width } satisfies CSSProperties)
                      : undefined
                  }
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={rowKey(row)} className="user-row">
                {columns.map((column) => (
                  <td key={column.id}>{column.render(row)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}
